/** BROADCAST TO PLAYERS — the switch at the top of Live Game View.
 *
 *  One tap turns the player board on, one tap turns it off. "Who is on the
 *  board" decides whose players are there; a super admin can also put other
 *  organizations on it, and only the ones that have switched sharing on.
 *  "Preview what players see" opens the player board itself.
 */
import { useCallback, useEffect, useState } from 'react';
import { Radio, Eye, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';

export const BroadcastControl = ({ orgId, neo = {}, t, isSuperAdmin = false }) => {
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        if (!orgId) return;
        try {
            const res = await api.get('/live-game/broadcast',
                { ...neo, params: { organization_id: orgId } });
            setData(res.data);
        } catch (_e) { /* a dropped read changes nothing on screen */ }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orgId]);

    useEffect(() => { load(); }, [load]);

    const save = async (patch) => {
        setBusy(true);
        try {
            const res = await api.put('/live-game/broadcast',
                { organization_id: orgId, ...patch }, neo);
            setData(res.data);
        } catch (e) {
            toast.error(e.response?.data?.detail === 'super_admin_only'
                ? t('liveGame.broadcastSuperOnly', 'Only a super admin can do that')
                : t('liveGame.broadcastFailed', 'Could not save that'));
        } finally { setBusy(false); }
    };

    if (!data) return null;
    const s = data.settings || {};
    const venues = data.venues || [];
    const orgs = data.organizations || [];
    const scopes = [
        ['location', t('liveGame.scopeThisLocation', 'This location')],
        ['locations', t('liveGame.scopeSomeLocations', 'Selected locations')],
        ['organization', t('liveGame.scopeOrganization', 'All locations of this organization')],
        ...(isSuperAdmin ? [
            ['organizations', t('liveGame.scopeSomeOrganizations', 'Selected organizations')],
            ['all_organizations', t('liveGame.scopeAllOrganizations', 'All organizations')],
        ] : []),
    ];

    const toggleIn = (key, id) => {
        const list = new Set(s[key] || []);
        if (list.has(id)) list.delete(id); else list.add(id);
        save({ [key]: [...list] });
    };

    return (
        <Card className="w-full" data-testid="live-game-broadcast">
            <CardContent className="p-4 space-y-3">
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <label className="flex items-center gap-2 text-sm font-semibold">
                        <input type="checkbox" checked={!!s.enabled} disabled={busy || !data.can_change}
                               onChange={(e) => save({ enabled: e.target.checked })}
                               data-testid="live-game-broadcast-switch" className="w-5 h-5" />
                        <Radio className="w-4 h-4 text-emerald-500" />
                        {t('liveGame.broadcast', 'Broadcast to players')}
                    </label>
                    {data.preview_card_id && (
                        <Button variant="outline" className="min-h-[44px]" asChild
                                data-testid="live-game-broadcast-preview">
                            <a href={`/goal/${data.preview_card_id}?board=1`} target="_blank" rel="noreferrer">
                                <Eye className="w-4 h-4 mr-2" />
                                {t('liveGame.broadcastPreview', 'Preview what players see')}
                            </a>
                        </Button>
                    )}
                </div>

                <div className="space-y-1">
                    <p className="text-xs text-muted-foreground">
                        {t('liveGame.broadcastWho', 'Who is on the board')}
                    </p>
                    <div className="flex flex-wrap gap-x-5 gap-y-2">
                        {scopes.map(([id, label]) => (
                            <label key={id} className="flex items-center gap-2 text-sm">
                                <input type="checkbox" checked={(s.scope || 'location') === id}
                                       disabled={busy || !data.can_change}
                                       onChange={() => save({ scope: id })}
                                       data-testid={`live-game-broadcast-scope-${id}`} />
                                {label}
                            </label>
                        ))}
                    </div>
                </div>

                {s.scope === 'locations' && (
                    <div className="flex flex-wrap gap-x-5 gap-y-2" data-testid="live-game-broadcast-venues">
                        {venues.length === 0 ? (
                            <p className="text-xs text-muted-foreground">
                                {t('liveGame.broadcastNoVenues', 'Nothing yet')}
                            </p>
                        ) : venues.map((v) => (
                            <label key={v.id} className="flex items-center gap-2 text-sm">
                                <input type="checkbox" checked={(s.location_ids || []).includes(v.id)}
                                       disabled={busy || !data.can_change}
                                       onChange={() => toggleIn('location_ids', v.id)}
                                       data-testid={`live-game-broadcast-venue-${v.id}`} />
                                {v.name}
                            </label>
                        ))}
                    </div>
                )}

                {s.scope === 'organizations' && isSuperAdmin && (
                    <div className="flex flex-wrap gap-x-5 gap-y-2" data-testid="live-game-broadcast-orgs">
                        {orgs.map((o) => (
                            <label key={o.id} className={`flex items-center gap-2 text-sm ${o.shared ? '' : 'opacity-50'}`}>
                                <input type="checkbox" checked={(s.organization_ids || []).includes(o.id)}
                                       disabled={busy || !data.can_change || !o.shared}
                                       onChange={() => toggleIn('organization_ids', o.id)}
                                       data-testid={`live-game-broadcast-org-${o.id}`} />
                                {o.name}
                                {!o.shared && (
                                    <span className="text-[11px]">
                                        {t('liveGame.broadcastNotShared', 'not sharing')}
                                    </span>
                                )}
                            </label>
                        ))}
                    </div>
                )}

                <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={!!s.share_on_boards}
                           disabled={busy || !data.can_change}
                           onChange={(e) => save({ share_on_boards: e.target.checked })}
                           data-testid="live-game-broadcast-share" />
                    {t('liveGame.broadcastShare', 'Share my players on broadcast boards')}
                </label>

                {/* LIVE CHAT — the Chat tab and the player profiles on that
                    board. A super admin's switch, off until they turn it on,
                    and one tap turns it off again. */}
                {isSuperAdmin && (
                    <div className="space-y-1 border-t border-border pt-3" data-testid="live-game-chat-switch-block">
                        <label className="flex items-center gap-2 text-sm font-semibold">
                            <input type="checkbox" checked={!!s.chat_enabled}
                                   disabled={busy || !data.can_change}
                                   onChange={(e) => save({ chat_enabled: e.target.checked })}
                                   data-testid="live-game-chat-switch" className="w-5 h-5" />
                            <MessageSquare className="w-4 h-4 text-sky-500" />
                            {t('liveGame.chatSwitch', 'Live chat')}
                        </label>
                        <p className="text-xs text-muted-foreground" data-testid="live-game-chat-switch-hint">
                            {t('liveGame.chatSwitchHint', 'A Chat tab and player profiles on the Live players board. Super admin only.')}
                        </p>
                    </div>
                )}
            </CardContent>
        </Card>
    );
};

export default BroadcastControl;
